import { useEffect, useState } from 'react';
import api from '../../api';

function slugFromTitle(title) {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function PageManager() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    heading: '',
    body: '',
    images: [],
    catalog_pdf: '',
    display_order: 0
  });
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const res = await api.get('/api/admin/pages');
      setPages(res.data);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (page = null) => {
    if (page) {
      setEditingPage(page);
      setFormData({
        title: page.title,
        slug: page.slug,
        heading: page.heading,
        body: page.body || '',
        images: page.images || [],
        catalog_pdf: page.catalog_pdf || '',
        display_order: page.display_order || 0
      });
    } else {
      setEditingPage(null);
      setFormData({
        title: '',
        slug: '',
        heading: '',
        body: '',
        images: [],
        catalog_pdf: '',
        display_order: pages.length
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPage(null);
  };

  const handleTitleChange = (title) => {
    setFormData(prev => ({
      ...prev,
      title,
      slug: editingPage ? prev.slug : slugFromTitle(title)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPage) {
        await api.put(`/api/admin/pages/${editingPage.id}`, formData);
      } else {
        await api.post('/api/admin/pages', formData);
      }
      closeModal();
      fetchPages();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save page');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    try {
      await api.delete(`/api/admin/pages/${id}`);
      fetchPages();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete page');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const res = await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, images: [...prev.images, res.data.url] }));
    } catch (err) {
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPdf(true);
    const fd = new FormData();
    fd.append('pdf', file);
    try {
      const res = await api.post('/api/admin/upload-pdf', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, catalog_pdf: res.data.url }));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to upload PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const clearPdf = () => {
    setFormData(prev => ({ ...prev, catalog_pdf: '' }));
  };

  if (loading) {
    return <div className="loading">Loading pages...</div>;
  }

  return (
    <div>
      <div className="admin-header">
        <h1>Pages</h1>
        <button onClick={() => openModal()} className="btn btn-primary">+ Add Page</button>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Order</th>
              <th>Title</th>
              <th>Slug</th>
              <th>Catalog</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(page => (
              <tr key={page.id}>
                <td>{page.display_order}</td>
                <td>{page.title}</td>
                <td style={{ color: 'var(--text-secondary)' }}>{page.slug}</td>
                <td>{page.catalog_pdf ? 'Yes' : '-'}</td>
                <td>
                  <button onClick={() => openModal(page)} className="btn btn-small btn-secondary" style={{ marginRight: '5px' }}>Edit</button>
                  <button onClick={() => handleDelete(page.id)} className="btn btn-small btn-danger">Delete</button>
                </td>
              </tr>
            ))}
            {pages.length === 0 && (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  No pages yet. Click "Add Page" to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <h2>{editingPage ? 'Edit Page' : 'Add Page'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => handleTitleChange(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Heading (shown on page) *</label>
                <input
                  type="text"
                  value={formData.heading}
                  onChange={e => setFormData({ ...formData, heading: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Body text</label>
                <textarea
                  value={formData.body}
                  onChange={e => setFormData({ ...formData, body: e.target.value })}
                  rows={5}
                />
              </div>
              <div className="form-group">
                <label>Images</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                {uploading && <span style={{ marginLeft: '10px', color: 'var(--text-secondary)' }}>Uploading...</span>}
                {formData.images.length > 0 && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                    {formData.images.map((img, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={img} alt="" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
                        <button type="button" onClick={() => removeImage(i)} style={{ position: 'absolute', top: -4, right: -4, width: 20, height: 20, borderRadius: '50%', background: '#e74c3c', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12 }}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Catalog PDF (link at bottom of page)</label>
                <input type="file" accept=".pdf,application/pdf" onChange={handlePdfUpload} disabled={uploadingPdf} />
                {uploadingPdf && <span style={{ marginLeft: '10px', color: 'var(--text-secondary)' }}>Uploading...</span>}
                {formData.catalog_pdf && (
                  <div style={{ marginTop: '8px' }}>
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Current: {formData.catalog_pdf}</span>
                    <button type="button" onClick={clearPdf} className="btn btn-small btn-secondary" style={{ marginLeft: '10px' }}>Remove PDF</button>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Display order</label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={e => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeModal} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary">{editingPage ? 'Save' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default PageManager;
